import { useState } from 'react'
import { gql } from 'graphql-request'
import { useForm } from 'react-hook-form'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"

import { useMutation } from '@hooks/graphql'
import TextEditor from '@components/TextEditor'
import SaveButton from './SaveButton'
import CounterInput from '@/components/ui/CounterInput'

import PreviewFreeResponseQuestionDialog from './PreviewFreeResponseQuestionDialog'

const UPDATE_FREE_RESPONSE_BLOCK_MUTATION = gql`
  mutation updateFreeResponseBlock($input: UpdateFreeResponseBlockInput!) {
    updateFreeResponseBlock(input: $input) {
      freeResponse {
        id
        question
        markAllocation
        markScheme
        workingsRequired
      }
      errors {
        message
      }
    }
  }
`

const FreeResponseQuestionForm = ({ id, question, markAllocation, markScheme, workingsRequired, refetch, uploadImage }) => {
  const [saving, setSaving] = useState(false)

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      question,
      markAllocation,
      markScheme,
      workingsRequired
    }
  })

  const watchedValues = form.watch()

  const { mutate: updateFreeResponseBlock, isLoading } = useMutation({
    gqlMutation: UPDATE_FREE_RESPONSE_BLOCK_MUTATION,
    onSuccess: () => {
      refetch()
      setSaving(false)
      form.reset(watchedValues)
    }
  })

  const handleSave = () => {
    setSaving(true)
    updateFreeResponseBlock({ input: { id, ...watchedValues } })
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(() => { })} className='w-full h-fit flex flex-col gap-3'>
          <FormField
            control={form.control}
            name="question"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Question</FormLabel>
                <FormControl>
                  <TextEditor.Container>
                    <TextEditor
                      uploadImage={uploadImage}
                      id='question'
                      {...field}
                    />
                    <TextEditor.Actions>
                      <TextEditor.ToolbarToggle />
                    </TextEditor.Actions>
                  </TextEditor.Container>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="markAllocation"
            rules={{
              required: 'Mark allocation is required',
              min: { value: 1, message: 'Mark allocation must be greater than 0' }
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mark allocation</FormLabel>
                <FormDescription>
                  The maximum mark allocation for this question
                </FormDescription>
                <FormControl>
                  <CounterInput
                    id='mark-allocation'
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="markScheme"
            rules={{ required: 'Mark scheme is required' }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mark scheme</FormLabel>
                <FormControl>
                  <TextEditor.Container>
                    <TextEditor
                      uploadImage={uploadImage}
                      id='mark-scheme'
                      {...field}
                    />
                    <TextEditor.Actions>
                      <TextEditor.ToolbarToggle />
                    </TextEditor.Actions>
                  </TextEditor.Container>
                </FormControl>
                <FormDescription>
                  Provide a mark scheme that includes a model answer, how to award marks etc.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='flex gap-3 justify-end items-center pb-3'>
            <PreviewFreeResponseQuestionDialog
              blockId={id}
              getValues={form.getValues}
            />

            <SaveButton
              saving={saving}
              hasChanges={form.formState.isDirty}
              isLoading={isLoading}
              onSave={handleSave}
            />
          </div>
        </form>
      </Form>
    </>
  )
}

export default FreeResponseQuestionForm
